import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { AddUserSkeleton } from './SkeletonLoaders';
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Grid,
  Divider,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  FormGroup,
  FormLabel,
  OutlinedInput,
  ListItemText
} from '@mui/material';
import { 
  AddCircleOutline, 
  RemoveCircleOutline, 
  Person as PersonIcon,
  ContactPhone as ContactIcon,
  Description as FormIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const AddUser = () => {
  const navigate = useNavigate();

  // Get selected location from AuthContext
  const { selectedBuilding } = useContext(AuthContext);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [caseManager, setCaseManager] = useState('');
  const [caseManagerEmail, setCaseManagerEmail] = useState('');
  const [caseManagerPhone, setCaseManagerPhone] = useState('');
  const [buildingLocations, setBuildingLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [contacts, setContacts] = useState([
    { contact_name: '', relationship: '', email: '', phone_number: '', notes: '' },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [submitStatus, setSubmitStatus] = useState({ success: false, error: false, message: '' });
  
  // Form selection state
  const [availableForms, setAvailableForms] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);

  // Date of issue for forms that use it (e.g., Dr Orders)
  const [drOrdersDateOfIssue, setDrOrdersDateOfIssue] = useState('');

  // Dr Orders form ID
  const DR_ORDERS_FORM_ID = 15;

  // Use selectedBuilding from AuthContext to set default location
  useEffect(() => {
    if (selectedBuilding && selectedBuilding.id && selectedBuilding.name !== "All") {
      setSelectedLocations([selectedBuilding.id]);
    }
  }, [selectedBuilding]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch locations
        const locationsResponse = await fetchWithAuth('/api/locations');
        const locationsData = await locationsResponse.json();
        
        // Filter out the "All" location if it exists in the API response
        const filteredLocations = Array.isArray(locationsData) 
          ? locationsData.filter(loc => loc.name !== "All") 
          : [];
        setBuildingLocations(filteredLocations);
        
        // Set default building location from AuthContext if available
        if (selectedBuilding && selectedBuilding.id && selectedBuilding.name !== "All") {
          setSelectedLocations([selectedBuilding.id]);
        } else if (filteredLocations.length > 0) {
          // Otherwise default to first location in the list
          setSelectedLocations([filteredLocations[0].id]);
        }

        // Fetch available report types/forms
        const formsResponse = await fetchWithAuth('/api/report-types');
        const formsData = await formsResponse.json();
        // Filter out archived forms
        const activeForms = Array.isArray(formsData)
          ? formsData.filter(form => !form.report_name?.includes('[ARCHIVED]'))
          : [];
        setAvailableForms(activeForms);

        // Default to common forms being selected (IDs: 3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 25)
        // Filter to only include IDs that exist in active forms
        const defaultFormIds = [3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 25];
        const activeFormIds = activeForms.map(f => f.id);
        const filteredDefaultIds = defaultFormIds.filter(id => activeFormIds.includes(id));
        setSelectedForms(filteredDefaultIds);
      } catch (error) {
        console.error('Error fetching data:', error);
        setSubmitStatus({
          success: false,
          error: true,
          message: 'Failed to load data. Please try refreshing the page.'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [selectedBuilding]);

  const handleContactChange = (index, field, value) => {
    const newContacts = contacts.map((contact, i) =>
      i === index ? { ...contact, [field]: value } : contact
    );
    setContacts(newContacts);
  };

  const addContact = () => {
    setContacts([...contacts, { contact_name: '', relationship: '', email: '', phone_number: '', notes: '' }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter((_, i) => i !== index));
    }
  };

  const handleFormToggle = (formId) => {
    setSelectedForms(prev => {
      if (prev.includes(formId)) {
        // Clear date of issue if Dr Orders is being unchecked
        if (formId === DR_ORDERS_FORM_ID) {
          setDrOrdersDateOfIssue('');
        }
        return prev.filter(id => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSelectAllForms = () => {
    if (selectedForms.length === availableForms.length) {
      setSelectedForms([]);
    } else {
      setSelectedForms(availableForms.map(form => form.id));
    }
  };

  const handleSubmit = async () => {
    setSubmitStatus({ success: false, error: false, message: '' });
    
    // Validate required fields
    if (!firstName.trim() || !lastName.trim()) {
      setSubmitStatus({
        success: false,
        error: true,
        message: 'First name and last name are required.'
      });
      return;
    }
    
    if (!phoneNumber.trim()) {
      setSubmitStatus({
        success: false,
        error: true,
        message: 'Phone number is required.'
      });
      return;
    }
    
    if (selectedLocations.length === 0) {
      setSubmitStatus({
        success: false,
        error: true,
        message: 'At least one building location is required.'
      });
      return;
    }

    // Validate Dr Orders date of issue if Dr Orders is selected
    if (selectedForms.includes(DR_ORDERS_FORM_ID) && !drOrdersDateOfIssue) {
      setSubmitStatus({
        success: false,
        error: true,
        message: 'Date of Issue is required for Doctor\'s Orders. Please select the date the orders were issued.'
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetchWithAuth('/api/individuals', {
        method: 'POST',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          address: address,
          case_manager: caseManager,
          case_manager_email: caseManagerEmail,
          case_manager_phone_number: caseManagerPhone,
          building_location_id: selectedLocations[0], // Primary location for backwards compatibility
          location_ids: selectedLocations, // All assigned locations
          contacts: contacts,
          selectedForms: selectedForms,
          // Include date of issue for Dr Orders if selected
          dr_orders_date_of_issue: selectedForms.includes(DR_ORDERS_FORM_ID) ? drOrdersDateOfIssue : null,
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Redirect to the new individual's profile page
        navigate(`/people/${data.individualId}`, {
          state: {
            message: `Individual added successfully! Generated ${data.formsGenerated || selectedForms.length} forms for ${new Date().getFullYear()}.`
          }
        });
      } else {
        setSubmitStatus({
          success: false,
          error: true,
          message: data.message || 'Error creating individual. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        success: false,
        error: true,
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        ADD NEW INDIVIDUAL
      </Typography>
      
      {submitStatus.success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {submitStatus.message}
        </Alert>
      )}
      
      {submitStatus.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitStatus.message}
        </Alert>
      )}
      
      {isLoading && !submitStatus.success && !submitStatus.error ? (
        <AddUserSkeleton />
      ) : (
        <Paper sx={{ p: 3, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          {/* Individual Information Section */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary">
                  Individual Information
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address (Optional)"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Street address, city, state, zip code"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Case Manager"
                    value={caseManager}
                    onChange={(e) => setCaseManager(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Case Manager Email"
                    value={caseManagerEmail}
                    onChange={(e) => setCaseManagerEmail(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Case Manager Phone Number"
                    value={caseManagerPhone}
                    onChange={(e) => setCaseManagerPhone(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="building-location-label">
                      Building Locations (Select one or more)
                    </InputLabel>
                    <Select
                      labelId="building-location-label"
                      multiple
                      value={selectedLocations}
                      onChange={(e) => setSelectedLocations(e.target.value)}
                      input={<OutlinedInput label="Building Locations (Select one or more)" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const location = buildingLocations.find(l => l.id === id);
                            return (
                              <Chip
                                key={id}
                                size="small"
                                icon={<LocationIcon />}
                                label={location?.name || id}
                                color={id === selectedBuilding?.id ? 'primary' : 'default'}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {buildingLocations.map((location) => (
                        <MenuItem
                          key={location.id}
                          value={location.id}
                          sx={{
                            fontWeight: location.id === selectedBuilding?.id ? 'bold' : 'normal',
                            bgcolor: location.id === selectedBuilding?.id ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                          }}
                        >
                          <Checkbox checked={selectedLocations.includes(location.id)} />
                          <ListItemText
                            primary={location.name}
                            secondary={location.id === selectedBuilding?.id && selectedBuilding.name !== "All" ? 'Pre-selected from navigation' : null}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Select all locations where this individual will receive services. The first selected location will be the primary.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Form Selection */}
          <Card variant="outlined" sx={{ mb: 4, bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FormIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary">
                  Forms Assignment
                </Typography>
              </Box>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                Select which forms this individual needs to complete. Forms will be created with appropriate due dates based on their renewal cycles.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleSelectAllForms}
                  startIcon={<AssignmentIcon />}
                >
                  {selectedForms.length === availableForms.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Chip
                  label={`${selectedForms.length} of ${availableForms.length} forms selected`}
                  color="primary"
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
              
              <FormControl component="fieldset" variant="standard" fullWidth>
                <FormGroup>
                  <Grid container spacing={2}>
                    {availableForms.map((form) => (
                      <Grid item xs={12} sm={6} md={4} key={form.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedForms.includes(form.id)}
                              onChange={() => handleFormToggle(form.id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: selectedForms.includes(form.id) ? 600 : 400 }}>
                                {form.report_name}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </FormControl>

              {/* Date of Issue for Dr Orders */}
              {selectedForms.includes(DR_ORDERS_FORM_ID) && (
                <Card variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderColor: 'warning.main' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <InfoIcon color="warning" sx={{ mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                        Doctor's Orders - Date of Issue Required
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enter the date the Doctor's Orders were issued. The form will be due for renewal 180 days from this date.
                      </Typography>
                      <TextField
                        type="date"
                        label="Date of Issue"
                        value={drOrdersDateOfIssue}
                        onChange={(e) => setDrOrdersDateOfIssue(e.target.value)}
                        size="small"
                        required
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          minWidth: 200,
                          bgcolor: 'white',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'warning.main' },
                            '&:hover fieldset': { borderColor: 'warning.dark' },
                            '&.Mui-focused fieldset': { borderColor: 'warning.dark' },
                          }
                        }}
                        helperText={drOrdersDateOfIssue ?
                          `Renewal due: ${new Date(new Date(drOrdersDateOfIssue).getTime() + 180 * 24 * 60 * 60 * 1000).toLocaleDateString()}` :
                          'Select the date from the doctor\'s orders document'
                        }
                      />
                    </Box>
                  </Box>
                </Card>
              )}
              
              {selectedForms.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Please select at least one form for this individual.
                  </Typography>
                </Alert>
              )}
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important Deadlines:</strong>
                  <br />
                  • <strong>Initial Submission:</strong> All forms must be submitted within <strong>90 days</strong> of onboarding
                  <br />
                  • <strong>Doctor's Orders:</strong> Due in <strong>180 days</strong> from today
                  <br />
                  • <strong>Annual Renewals:</strong> After initial submission, forms renew annually (except Doctor's Orders which renews every 180 days)
                </Typography>
              </Alert>
            </CardContent>
          </Card>
          
          {/* Contacts Section */}
          <Card variant="outlined" sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ContactIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary">
                  Contacts
                </Typography>
              </Box>
              
              {contacts.map((contact, index) => (
                <Card 
                  key={index} 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    p: 2,
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { boxShadow: 1 }
                  }}
                >
                  <IconButton 
                    onClick={() => removeContact(index)} 
                    color="error"
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      right: 8, 
                      top: 8,
                      visibility: contacts.length > 1 ? 'visible' : 'hidden'
                    }}
                  >
                    <RemoveCircleOutline />
                  </IconButton>
                  
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Contact #{index + 1}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Contact Name"
                        value={contact.contact_name}
                        onChange={(e) => handleContactChange(index, 'contact_name', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Relationship"
                        value={contact.relationship}
                        onChange={(e) => handleContactChange(index, 'relationship', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={contact.email}
                        onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={contact.phone_number}
                        onChange={(e) => handleContactChange(index, 'phone_number', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Notes"
                        value={contact.notes || ''}
                        onChange={(e) => handleContactChange(index, 'notes', e.target.value)}
                        size="small"
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}
              
              <Button 
                startIcon={<AddCircleOutline />} 
                onClick={addContact} 
                sx={{ mt: 1 }}
                variant="outlined"
              >
                Add Another Contact
              </Button>
            </CardContent>
          </Card>
          
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={isLoading}
            size="large"
            sx={{ 
              py: 1.5,
              fontSize: '1rem',
              boxShadow: 3,
              '&:hover': { boxShadow: 5 }
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default AddUser;
